import d from './DOM.js';

export default class Help {
	private static searchInput: HTMLInputElement;
	private static clearButton: HTMLButtonElement;
	private static printButton: HTMLButtonElement;
	private static topicMarkers: HTMLElement[];
	private static helpSections: HTMLElement[];
	private static defaultMessage: HTMLElement;
	private static originalContent: { [id: string]: string } = {};
	
	public static init() {
		this.searchInput = d.get<HTMLInputElement>('help-search-input');
		this.clearButton = d.get<HTMLButtonElement>('help-search-clear');
		this.printButton = d.get<HTMLButtonElement>('help-print-btn');
		this.topicMarkers = d.qsa<HTMLElement>('.topic-marker');
		this.helpSections = d.qsa<HTMLElement>('.help-section');
		this.defaultMessage = d.get<HTMLElement>('help-default-message');
		
		// Cache original content (only if not already cached to prevent capturing highlights on re-init)
		this.helpSections.forEach(section => {
			if (!this.originalContent[section.id]) { this.originalContent[section.id] = section.innerHTML; }
		});
		
		this.initEventListeners();
		this.initImageClickHandlers();
	}
	
	private static initEventListeners() {
		// Search Input
		d.e('input', () => this.handleSearch(), this.searchInput);
		d.e('click', () => this.searchInput.focus(), this.searchInput);
		
		// Clear Button
		d.e('click', () => this.clearSearch(), this.clearButton);
		
		// Print Button
		d.e('click', () => this.handlePrint(), this.printButton);
		
		// Topic Markers
		this.topicMarkers.forEach(marker => {
			d.e('click', () => {
				const targetID = marker.getAttribute('data-target');
				if (targetID) { this.showTopic(targetID); }
			}, marker);
		});
	}
	
	private static initImageClickHandlers() {
		const images = d.qsa<HTMLImageElement>('.help-thumbnail');
		
		images.forEach(img => {
			d.e('click', () => { window.open(img.src, '_blank'); }, img);
		});
	}
	
	private static handlePrint() {
		const printWindow = window.open('', '_blank');
		
		if (!printWindow) {
			alert('Please allow popups to print the help documentation.');
			return;
		}

		let contentHtml = '';
		
		this.topicMarkers.forEach(marker => {
			const targetID = marker.getAttribute('data-target');
			
			if (targetID && this.originalContent[targetID]) {
				contentHtml += `
					<div class="print-section">
						<div class="section-content">
							${this.originalContent[targetID]}
						</div>
						<hr>
						<div class="force-page-break"></div>
					</div>
				`;
			}
		});
		
		const fullHtml = `
			<!DOCTYPE html>
			<html>
			<head>
				<title>TradeVision Help Documentation</title>
				<style>
					body { font-family: sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
					h1 { color: #000; border-bottom: 2px solid #ccc; padding-bottom: 10px; margin-top: 40px; }
					img { max-width: 100%; height: auto; }
					hr { border: 0; border-top: 1px solid #eee; margin: 30px 0; }
					.print-section { page-break-inside: avoid; }
					@media print {
						.print-section { page-break-inside: avoid; }
						h1 { page-break-before: always; }
						h1:first-of-type { page-break-before: auto; }
						
						.force-page-break {
							break-after: page;
						}
						
						table, figure, .keep-together {
							break-inside: avoid;
						}
					}

				</style>
			</head>
			<body>
				<div style="text-align: center; margin-bottom: 40px;">
					<h1>TradeVision Help Center</h1>
					<p>Generated on ${new Date().toLocaleDateString()}</p>
				</div>
				${contentHtml}
				<script>
					window.onload = () => {
						window.print();
						window.close();
					};
				</script>
			</body>
			</html>
		`;
		
		printWindow.document.write(fullHtml);
		printWindow.document.close();
	}
	
	private static handleSearch() {
		const query = this.searchInput.value.toLowerCase().trim();
		
		if (query.length > 0) {
			this.clearButton.style.display = 'block';
			this.performSearch(query);
		} else {
			this.clearButton.style.display = 'none';
			this.resetView();
		}
	}
	
	private static performSearch(query: string) {
		// Hide all markers initially
		this.topicMarkers.forEach(marker => marker.classList.add('hidden'));
		let foundAny = false;
		
		this.topicMarkers.forEach(marker => {
			const targetID = marker.getAttribute('data-target');
			if (!targetID) { return; }
			
			const contentDiv = d.get<HTMLElement>(targetID);
			if (!contentDiv) { return; }
			
			// Reset content to original
			contentDiv.innerHTML = this.originalContent[targetID];
			
			// Highlight matches in content
			const hasMatch = this.highlightTextInElement(contentDiv, query);
			const markerMatch = marker.textContent?.toLowerCase().includes(query);
			
			if (hasMatch || markerMatch) {
				marker.classList.remove('hidden');
				foundAny = true;
			}
		});
		
		// Re-bind image click handlers since we reset innerHTML
		this.initImageClickHandlers();
	}
	
	private static highlightTextInElement(element: HTMLElement, query: string): boolean {
		if (!query) { return false; }
		
		const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
		const textNodes: Node[] = [];
		
		let node;
		while (node = walker.nextNode()) { textNodes.push(node); }
		
		let found = false;
		const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
		
		textNodes.forEach(textNode => {
			if (textNode.nodeValue && textNode.nodeValue.toLowerCase().includes(query)) {
				found = true;
				const newHTML = textNode.nodeValue.replace(regex, '<span class="search-highlight">$1</span>');
				const wrapper = document.createElement('span');
				wrapper.innerHTML = newHTML;
				
				if (textNode.parentNode) {
					while (wrapper.firstChild) { textNode.parentNode.insertBefore(wrapper.firstChild, textNode); }
					textNode.parentNode.removeChild(textNode);
				}
			}
		});
		
		return found;
	}
	
	private static clearSearch() {
		this.searchInput.value = '';
		this.clearButton.style.display = 'none';
		
		// Restore original content
		this.helpSections.forEach(section => { section.innerHTML = this.originalContent[section.id]; });
			
		this.initImageClickHandlers();
		this.resetView();
	}

	private static resetView() {
		// Show all markers
		this.topicMarkers.forEach(marker => marker.classList.remove('hidden'));
	}
	
	private static showTopic(targetID: string) {
		// Update markers UI
		this.topicMarkers.forEach(marker => {
			if (marker.getAttribute('data-target') === targetID) { marker.classList.add('active'); }
			else { marker.classList.remove('active'); }
		});
		
		// Update content UI
		this.helpSections.forEach(section => {
			if (section.id === targetID) {
				section.classList.remove('hidden');
				section.classList.add('active');
				
				// Scroll to first highlight if exists
				const firstHighlight = section.querySelector('.search-highlight');
				
				if (firstHighlight) {
					firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
				} else {
					// Scroll to top if no highlight
					section.scrollTop = 0;
					// Also ensure the parent container is scrolled to top if needed, 
					// but here section is the container? No, help-content-pane is the container.
					// Actually help-section is inside help-content-pane.
					// help-content-pane has overflow-y: auto.
					// So we need to scroll help-content-pane.
					const container = d.qs<HTMLElement>('.help-content-pane');
					if (container && !firstHighlight) { container.scrollTop = 0; }
				}
			} else {
				section.classList.add('hidden');
				section.classList.remove('active');
			}
		});
		
		// Hide default message if it's not the target (it shouldn't be a target of a marker usually, but good to be safe)
		if (targetID !== 'help-default-message') {
			this.defaultMessage.classList.add('hidden');
			this.defaultMessage.classList.remove('active');
		}
	}
}
